import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, KeyRound, Mail, Eye, EyeOff, CheckCircle, Loader2, Send, ShieldCheck, Gamepad2, Trophy, Swords } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Czy użytkownik przyszedł z linku resetowania hasła?
  const fromRecovery = location.search.includes('from=recovery') || location.hash.includes('from=recovery');

  // ── Zmiana hasła ──
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [changingPass, setChangingPass] = useState(false);
  const [passChanged, setPassChanged] = useState(false);

  // ── Reset przez e-mail ──
  const [resetEmail, setResetEmail] = useState(user?.email ?? '');
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // ── Statystyki gry ──
  const [gameStats, setGameStats] = useState<{figurines:number; bestScore:number; sessions:number} | null>(null);

  useEffect(() => {
    if (!user) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: prof } = await (supabase as any)
        .from('profiles').select('game_figurines,game_best_score').eq('id', user.id).single();
      const { count } = await (supabase as any)
        .from('game_sessions').select('*', { count: 'exact', head: true }).eq('user_id', user.id);
      setGameStats({
        figurines: prof?.game_figurines ?? 0,
        bestScore: prof?.game_best_score ?? 0,
        sessions: count ?? 0,
      });
    })();
  }, [user]);

  // Walidacja hasła
  const tooShort = newPass.length > 0 && newPass.length < 8;
  const mismatch = confirmPass.length > 0 && newPass !== confirmPass;
  const canChange = newPass.length >= 8 && newPass === confirmPass;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canChange) return;
    setChangingPass(true);
    const { error } = await supabase.auth.updateUser({ password: newPass });
    if (error) {
      toast.error('Błąd: ' + error.message);
    } else {
      setPassChanged(true);
      setNewPass('');
      setConfirmPass('');
      toast.success('Hasło zostało zmienione ✓');
    }
    setChangingPass(false);
  };

  const handleSendReset = async () => {
    if (!resetEmail.trim()) return;
    setSendingReset(true);
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/#/profile?from=recovery`,
    });
    if (error) {
      toast.error('Błąd: ' + error.message);
    } else {
      setResetSent(true);
      toast.success('Link resetowania hasła wysłany na ' + resetEmail);
    }
    setSendingReset(false);
  };

  const strength = (() => {
    const checks = [newPass.length >= 8, /[A-Z]/.test(newPass), /[0-9]/.test(newPass), /[^A-Za-z0-9]/.test(newPass)];
    return checks.filter(Boolean).length;
  })();
  const strengthColors = ['oklch(0.62 0.28 15)', 'oklch(0.72 0.24 45)', 'oklch(0.65 0.26 150)', 'oklch(0.65 0.26 150)'];
  const strengthLabels = ['Słabe', 'Przeciętne', 'Dobre', 'Silne'];

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <div className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3 border-b"
        style={{ background: 'oklch(0.10 0.022 280 / 0.92)', borderColor: 'oklch(0.22 0.04 280)', backdropFilter: 'blur(14px)' }}>
        <button onClick={() => navigate('/')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm font-semibold"
          style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          <ArrowLeft className="w-4 h-4" /> Strona główna
        </button>
        <span className="text-muted-foreground/40">/</span>
        <span className="font-black tracking-widest text-foreground text-sm" style={{ fontFamily: 'Orbitron, sans-serif' }}>
          MÓJ PROFIL
        </span>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8 space-y-6">

        {/* Baner "z linku resetu" */}
        {fromRecovery && !passChanged && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 p-4 rounded-xl border"
            style={{ background: 'oklch(0.65 0.32 280 / 0.12)', borderColor: 'oklch(0.65 0.32 280 / 0.4)' }}>
            <ShieldCheck className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--primary)' }} />
            <p className="text-sm font-semibold" style={{ fontFamily: 'Rajdhani, sans-serif', color: 'var(--primary)' }}>
              Kliknąłeś link resetowania hasła — ustaw teraz nowe hasło poniżej.
            </p>
          </motion.div>
        )}

        {/* Karta — info o koncie */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="rounded-2xl border p-5"
          style={{ background: 'oklch(0.15 0.03 280)', borderColor: 'oklch(0.23 0.04 280)' }}>
          <h2 className="text-xs font-bold tracking-widest text-muted-foreground mb-4"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}>INFORMACJE O KONCIE</h2>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center font-black text-xl text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}>
              {(profile?.username?.[0] ?? user?.email?.[0] ?? '?').toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-black text-foreground text-base truncate"
                style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.85rem' }}>
                {profile?.username ?? 'Użytkownik'}
              </p>
              <p className="text-sm text-muted-foreground truncate flex items-center gap-1.5 mt-0.5"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                {user?.email}
              </p>
              {profile?.role === 'admin' && (
                <span className="inline-block mt-1.5 text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ background: 'var(--primary)', color: 'white', fontFamily: 'JetBrains Mono, monospace' }}>
                  ADMINISTRATOR
                </span>
              )}
            </div>
          </div>
        </motion.div>

        {/* Karta — statystyki gry */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="rounded-2xl border p-5"
          style={{ background: 'oklch(0.13 0.04 290)', borderColor: 'oklch(0.26 0.08 290 / 0.7)' }}>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <Gamepad2 className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              <h2 className="text-xs font-bold tracking-widest text-muted-foreground"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}>STATYSTYKI GRY</h2>
            </div>
            <Link to="/game"
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-lg transition-all hover:scale-105"
              style={{ fontFamily: 'Orbitron, sans-serif', background: 'linear-gradient(135deg,#c0a020,#f0c040)', color: '#000' }}>
              🎮 ZAGRAJ
            </Link>
          </div>

          {gameStats === null ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span style={{ fontFamily: 'Rajdhani, sans-serif' }}>Ładowanie statystyk...</span>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {/* Figurki */}
              <div className="flex flex-col items-center gap-1.5 p-3 rounded-xl"
                style={{ background: 'oklch(0.18 0.055 60 / 0.5)', border: '1px solid oklch(0.35 0.10 60 / 0.5)' }}>
                <span className="text-2xl leading-none">🏆</span>
                <span className="text-2xl font-black tabular-nums" style={{ fontFamily: 'Orbitron, sans-serif', color: '#f0c020', textShadow: '0 0 12px rgba(240,192,32,0.5)' }}>
                  {gameStats.figurines}
                </span>
                <span className="text-xs text-muted-foreground text-center leading-tight" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  zebr.<br/>figurek
                </span>
              </div>

              {/* Najlepszy wynik */}
              <div className="flex flex-col items-center gap-1.5 p-3 rounded-xl"
                style={{ background: 'oklch(0.18 0.055 290 / 0.5)', border: '1px solid oklch(0.35 0.10 290 / 0.5)' }}>
                <Trophy className="w-6 h-6" style={{ color: 'var(--primary)' }} />
                <span className="text-2xl font-black tabular-nums" style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--primary)', textShadow: '0 0 12px oklch(0.65 0.32 280 / 0.4)', fontSize: gameStats.bestScore > 9999 ? '1.1rem' : '1.5rem' }}>
                  {gameStats.bestScore}
                </span>
                <span className="text-xs text-muted-foreground text-center leading-tight" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  najlepszy<br/>wynik
                </span>
              </div>

              {/* Rozegrane sesje */}
              <div className="flex flex-col items-center gap-1.5 p-3 rounded-xl"
                style={{ background: 'oklch(0.18 0.04 160 / 0.5)', border: '1px solid oklch(0.35 0.08 160 / 0.5)' }}>
                <Swords className="w-6 h-6" style={{ color: '#50d090' }} />
                <span className="text-2xl font-black tabular-nums" style={{ fontFamily: 'Orbitron, sans-serif', color: '#50d090', textShadow: '0 0 12px rgba(80,208,144,0.4)' }}>
                  {gameStats.sessions}
                </span>
                <span className="text-xs text-muted-foreground text-center leading-tight" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  rozegranych<br/>sesji
                </span>
              </div>
            </div>
          )}

          {gameStats !== null && gameStats.figurines === 0 && (
            <p className="mt-4 text-sm text-center text-muted-foreground/60" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              Jeszcze nie zebrałeś żadnej figurki — zagraj i zbieraj! 💀
            </p>
          )}
        </motion.div>

        {/* Karta — zmiana hasła */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="rounded-2xl border p-5"
          style={{ background: 'oklch(0.15 0.03 280)', borderColor: fromRecovery ? 'oklch(0.65 0.32 280 / 0.5)' : 'oklch(0.23 0.04 280)' }}>
          <div className="flex items-center gap-2.5 mb-5">
            <KeyRound className="w-4 h-4" style={{ color: 'var(--primary)' }} />
            <h2 className="text-xs font-bold tracking-widest text-muted-foreground"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}>ZMIANA HASŁA</h2>
          </div>

          {passChanged ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle className="w-12 h-12 text-green-400" />
              <p className="font-bold text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Hasło zostało zmienione pomyślnie!
              </p>
              <button onClick={() => setPassChanged(false)}
                className="text-sm text-primary hover:underline" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Zmień ponownie
              </button>
            </div>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              {/* Nowe hasło */}
              <div>
                <label className="block text-xs font-bold tracking-widest text-muted-foreground mb-2"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}>NOWE HASŁO</label>
                <div className="relative">
                  <Input type={showNew ? 'text' : 'password'} value={newPass}
                    onChange={e => setNewPass(e.target.value)}
                    placeholder="Min. 8 znaków"
                    className="pr-10 border-border/60 bg-[oklch(0.13_0.028_280)] text-foreground placeholder:text-muted-foreground/40 focus:border-primary"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                  <button type="button" onClick={() => setShowNew(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {tooShort && (
                  <p className="text-xs text-destructive mt-1.5" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                    Hasło musi mieć co najmniej 8 znaków
                  </p>
                )}
                {newPass.length >= 8 && (
                  <div className="mt-2 space-y-1">
                    <div className="flex gap-1">
                      {[0,1,2,3].map(i => (
                        <div key={i} className="flex-1 h-1 rounded-full transition-all duration-300"
                          style={{ background: i < strength ? strengthColors[strength-1] : 'oklch(0.22 0.04 280)' }} />
                      ))}
                    </div>
                    <p className="text-xs" style={{ color: strengthColors[strength-1], fontFamily: 'Rajdhani, sans-serif' }}>
                      Siła: <strong>{strengthLabels[strength-1]}</strong>
                    </p>
                  </div>
                )}
              </div>

              {/* Potwierdź hasło */}
              <div>
                <label className="block text-xs font-bold tracking-widest text-muted-foreground mb-2"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}>POTWIERDŹ HASŁO</label>
                <div className="relative">
                  <Input type={showConfirm ? 'text' : 'password'} value={confirmPass}
                    onChange={e => setConfirmPass(e.target.value)}
                    placeholder="Powtórz nowe hasło"
                    className="pr-10 border-border/60 bg-[oklch(0.13_0.028_280)] text-foreground placeholder:text-muted-foreground/40 focus:border-primary"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                  <button type="button" onClick={() => setShowConfirm(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {mismatch && (
                  <p className="text-xs text-destructive mt-1.5" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                    Hasła nie są identyczne
                  </p>
                )}
                {confirmPass.length > 0 && !mismatch && (
                  <p className="text-xs text-green-400 mt-1.5 flex items-center gap-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                    <CheckCircle className="w-3 h-3" /> Hasła są identyczne
                  </p>
                )}
              </div>

              <Button type="submit" disabled={!canChange || changingPass}
                className="w-full gap-2 font-black tracking-wider py-5 text-sm disabled:opacity-40"
                style={{
                  fontFamily: 'Orbitron, sans-serif',
                  background: canChange ? 'linear-gradient(135deg, var(--primary), var(--accent))' : 'oklch(0.22 0.04 280)',
                  boxShadow: canChange ? '0 0 20px oklch(0.65 0.32 280 / 0.3)' : 'none',
                  transition: 'all 0.2s',
                }}>
                {changingPass
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> ZAPISYWANIE...</>
                  : <><KeyRound className="w-4 h-4" /> USTAW NOWE HASŁO</>}
              </Button>
            </form>
          )}
        </motion.div>

        {/* Karta — reset przez e-mail */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="rounded-2xl border p-5"
          style={{ background: 'oklch(0.15 0.03 280)', borderColor: 'oklch(0.23 0.04 280)' }}>
          <div className="flex items-center gap-2.5 mb-5">
            <Send className="w-4 h-4" style={{ color: 'var(--accent)' }} />
            <h2 className="text-xs font-bold tracking-widest text-muted-foreground"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}>WYŚLIJ LINK RESETOWANIA HASŁA</h2>
          </div>

          {resetSent ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle className="w-12 h-12 text-green-400" />
              <p className="font-bold text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Link wysłany na <span style={{ color: 'var(--accent)' }}>{resetEmail}</span>
              </p>
              <p className="text-sm text-muted-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Sprawdź skrzynkę e-mail i kliknij link — zostaniesz przekierowany z powrotem tutaj.
              </p>
              <button onClick={() => setResetSent(false)}
                className="text-sm text-primary hover:underline" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Wyślij ponownie
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Na podany adres e-mail zostanie wysłany link do zresetowania hasła.
                Po kliknięciu w link wrócisz na tę stronę i będziesz mógł ustawić nowe hasło.
              </p>
              <div>
                <label className="block text-xs font-bold tracking-widest text-muted-foreground mb-2"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}>ADRES E-MAIL</label>
                <Input value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                  type="email" placeholder="twoj@email.com"
                  className="border-border/60 bg-[oklch(0.13_0.028_280)] text-foreground placeholder:text-muted-foreground/40 focus:border-primary"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }} />
              </div>
              <Button onClick={handleSendReset} disabled={!resetEmail.trim() || sendingReset}
                variant="outline" className="w-full gap-2 font-bold border-accent/40 hover:border-accent/80"
                style={{ fontFamily: 'Rajdhani, sans-serif', color: 'var(--accent)', fontSize: '0.9rem' }}>
                {sendingReset
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> WYSYŁANIE...</>
                  : <><Send className="w-4 h-4" /> WYŚLIJ LINK RESETOWANIA</>}
              </Button>
            </div>
          )}
        </motion.div>

      </div>
    </div>
  );
}
